/**
 * PATH: packages/ui/src/kanban/components/card-detail/openModePref.ts
 * FILE_ID: UI-KANBAN-CMP-OPENMODE-PREF-001
 * PURPOSE: Per-user "card open mode" preference — clicking a card opens
 *          the standard drawer (default) or a Trello-style full-screen
 *          popup of the SAME drawer content. Storage + a hook that keeps
 *          every mounted drawer in sync.
 * WHY: ARC-1 §4.4 (U14). Client: some operators want the Trello popup
 *      feel; others want the split-pane drawer. One component, two
 *      chromes — the pref only swaps wrapper/aside chrome strings in
 *      useCardDrawerLayout, never the drawer body.
 * FLOW:
 *   ┌────────────┐   ┌──────────────────────────┐   ┌─────────────────┐
 *   │ userId     │ → │ localStorage             │ → │ 'drawer'|'popup'│
 *   │ + toggle   │   │ kanban:prefs:<userId>    │   │ + change event  │
 *   └────────────┘   └──────────────────────────┘   └─────────────────┘
 * DEPENDENCIES:
 *   - React useSyncExternalStore: tear-free reads across drawer mounts.
 * RELATED:
 *   - useCardDrawerLayout.ts: consumes the mode for chrome selection.
 *   - drawer-sections/DrawerHeader.tsx: renders the toggle button.
 *   - ARC-4 §4.4: shares the `kanban:prefs:<userId>` JSON bag — add new
 *     keys to the SAME object, do not mint a second storage key.
 */
import { useCallback, useSyncExternalStore } from 'react'

export type KanbanOpenMode = 'drawer' | 'popup'

/** Cross-mount + cross-package notify channel (storage events don't fire
 *  in the writing tab, so we dispatch our own). */
const CHANGE_EVENT = 'kanban:open-mode-changed'

/** One JSON bag per user so ARC-4 prefs can join it without new keys.
 *
 *  BUG-2026-07-03-KANBAN-OPEN-MODE-DEAD-WITHOUT-IDENTITY: hosts where the
 *  auth session hydrates `user: null` (the master SPA's kanban bridge does
 *  on some sessions) used to hit the "no identity → write is a no-op"
 *  guard, which made the header toggle silently DEAD. Identity absence
 *  must degrade to a device-scoped pref, never disable the control —
 *  same `?? 'anon'` convention as board-list/storage.ts. */
function userSegment(userId: string | number | null | undefined): string {
  return userId === null || userId === undefined || userId === ''
    ? 'anon'
    : String(userId)
}

function storageKey(userId: string | number | null | undefined): string {
  return `kanban:prefs:${userSegment(userId)}`
}

function readPrefsBag(
  userId: string | number | null | undefined,
): Record<string, unknown> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(storageKey(userId))
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : {}
  } catch {
    return {}
  }
}

const OUTSIDE_CLICK_EVENT = 'kanban:close-outside-changed'

/**
 * Missing key means on. Operators opt out from Settings → Kanban.
 */
export function readCloseDrawerOnOutsideClick(
  userId: string | number | null | undefined,
): boolean {
  const value = readPrefsBag(userId).closeOnOutsideClick
  return value === false ? false : true
}

export function writeCloseDrawerOnOutsideClick(
  userId: string | number | null | undefined,
  enabled: boolean,
): void {
  if (typeof window === 'undefined') return
  try {
    const bag = readPrefsBag(userId)
    bag.closeOnOutsideClick = enabled
    window.localStorage.setItem(storageKey(userId), JSON.stringify(bag))
  } catch {
    /* quota / disabled storage — this session still updates via the event. */
  }
  window.dispatchEvent(new CustomEvent(OUTSIDE_CLICK_EVENT))
}

/**
 * [enabled, setEnabled]. Default is on until the operator saves off.
 * Shares the `kanban:prefs:<userId>` bag with the open-mode preference.
 */
export function useCloseDrawerOnOutsideClick(
  userId: string | number | null | undefined,
): [boolean, (next: boolean) => void] {
  const subscribe = useCallback((onChange: () => void) => {
    if (typeof window === 'undefined') return () => {}
    window.addEventListener(OUTSIDE_CLICK_EVENT, onChange)
    window.addEventListener('storage', onChange)
    return () => {
      window.removeEventListener(OUTSIDE_CLICK_EVENT, onChange)
      window.removeEventListener('storage', onChange)
    }
  }, [])
  const getSnapshot = useCallback(
    () => readCloseDrawerOnOutsideClick(userId),
    [userId],
  )
  const enabled = useSyncExternalStore(subscribe, getSnapshot, () => true)
  const setEnabled = useCallback(
    (next: boolean) => writeCloseDrawerOnOutsideClick(userId, next),
    [userId],
  )
  return [enabled, setEnabled]
}

export function readKanbanOpenMode(
  userId: string | number | null | undefined,
): KanbanOpenMode {
  const bag = readPrefsBag(userId)
  return bag.openMode === 'popup' ? 'popup' : 'drawer'
}

export function writeKanbanOpenMode(
  userId: string | number | null | undefined,
  mode: KanbanOpenMode,
): void {
  if (typeof window === 'undefined') return
  try {
    const bag = readPrefsBag(userId)
    bag.openMode = mode
    window.localStorage.setItem(storageKey(userId), JSON.stringify(bag))
  } catch {
    /* quota / disabled storage — the event below still updates this
       session's mounted drawers; the pref just won't survive reload. */
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

/**
 * [mode, setMode] backed by localStorage + a change event, so a toggle in
 * one drawer instance repaints every other mounted drawer immediately.
 * `userId = null` (host without identity) scopes the pref to the shared
 * `anon` bag — device-scoped rather than user-scoped, but never dead.
 */
export function useKanbanOpenMode(
  userId: string | number | null | undefined,
): [KanbanOpenMode, (next: KanbanOpenMode) => void] {
  const subscribe = useCallback((onChange: () => void) => {
    if (typeof window === 'undefined') return () => {}
    window.addEventListener(CHANGE_EVENT, onChange)
    // Cross-tab sync comes for free via the storage event.
    window.addEventListener('storage', onChange)
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange)
      window.removeEventListener('storage', onChange)
    }
  }, [])
  const getSnapshot = useCallback(() => readKanbanOpenMode(userId), [userId])
  const mode = useSyncExternalStore(subscribe, getSnapshot, () => 'drawer' as const)
  const setMode = useCallback(
    (next: KanbanOpenMode) => writeKanbanOpenMode(userId, next),
    [userId],
  )
  return [mode, setMode]
}
